import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from 'src/environments/environment';
import {
  clearStoredAuthTokens,
  decodeJwtPayload,
  isJwtExpired,
  navigateToForbidden,
} from './shared/services/token.util';
import { Router } from '@angular/router';
import { map, catchError, switchMap, take } from 'rxjs/operators';
import { AuthRedirectGuard } from './shared/services/auth-redirect.guard';

@Injectable()
export class AuthInterceptorInterceptor implements HttpInterceptor {
  constructor(private auth: AuthService,
    private router: Router,
    private authRedirectGuard: AuthRedirectGuard) {}

  ngOnInit() {}

  // intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

  //   const idToken = localStorage.getItem('idToken');
  //   const accessToken = localStorage.getItem('accessToken');

  //   console.log("idToken", idToken)
  //   console.log("accessToken", accessToken)

  //   const authRequest = request.clone({
  //     setHeaders: {
  //       id_token: idToken,
  //       Authorization: `Bearer ${accessToken}`,
  //     }
  //   })
  //   return next.handle(authRequest);
  // }

 intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const domain = environment.apiDomain;
    const dashboardDomain = (environment.dashboardApiBaseUrl ?? '').replace(/\/$/, '');
    const version = environment.verion;
    const app = environment.appName;
    const baseUrl = `${domain}/${app}/${version}`;

    const isAbsoluteUrl =
      req.url.startsWith('https://') || req.url.startsWith('http://');
    const isApiRequest =
      req.url.startsWith(domain) ||
      (!!dashboardDomain && req.url.startsWith(dashboardDomain));

    // Ignore only non-API absolute URLs (CDNs, third-party services, etc.)
    if (isAbsoluteUrl && !isApiRequest) {
      return next.handle(req);
    }

    const attachToken = (accessToken: string, idToken: string) => {
      console.log('[BasePathInterceptor] Access token available:', !!accessToken);
      this.logIdTokenDiagnostics(idToken);
      const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
      };

      if (idToken) {
        headers['id_token'] = idToken;
      }

      if (isAbsoluteUrl) {
        return req.clone({ setHeaders: headers });
      }

      return req.clone({
        url: baseUrl + req.url,
        setHeaders: headers,
      });
    };

    const handleApiError = (error: unknown) => {
      const httpError = error as { status?: number };

      if (httpError.status === 403) {
        navigateToForbidden(this.router);
      } else if (httpError.status === 401) {
        this.authRedirectGuard.redirectForTokenError({
          error: 'login_required',
          message: 'API returned 401 Unauthorized',
        });
      }

      return throwError(() => error);
    };

    if (this.authRedirectGuard.hasExpiredStoredTokens()) {
      clearStoredAuthTokens();
      this.authRedirectGuard.redirectForTokenError({
        error: 'login_required',
        message: 'Stored access or id token expired',
      });
      return throwError(() => ({
        error: 'login_required',
        message: 'Stored access or id token expired',
      }));
    }

    return this.auth.getAccessTokenSilently(this.getSilentTokenOptions() as any).pipe(
      map((tokenResponse: any) => tokenResponse?.access_token ?? tokenResponse),
      catchError((tokenError) => {
        const cached = localStorage.getItem('accessToken');
        if (cached && !isJwtExpired(cached)) {
          return of(cached);
        }
        if (cached) {
          clearStoredAuthTokens();
        }
        return throwError(() => tokenError);
      }),
      switchMap((accessToken) => {
        if (!accessToken || isJwtExpired(accessToken)) {
          clearStoredAuthTokens();
          const expiredError = {
            error: 'login_required',
            message: 'Access token expired',
          };
          this.authRedirectGuard.redirectForTokenError(expiredError);
          return throwError(() => expiredError);
        }

        return this.resolveIdToken().pipe(
          switchMap((idToken) => {
            if (!idToken || isJwtExpired(idToken)) {
              const idTokenError = {
                error: 'login_required',
                message: 'Unable to resolve valid id token',
              };
              this.authRedirectGuard.redirectForTokenError(idTokenError);
              return throwError(() => idTokenError);
            }

            return next
              .handle(attachToken(accessToken, idToken))
              .pipe(catchError(handleApiError));
          })
        );
      }),
      catchError((tokenError) => {
        console.error('Token fetch error:', tokenError);
        this.authRedirectGuard.redirectForTokenError(tokenError);
        return throwError(() => tokenError);
      })
    );
  }

  private logIdTokenDiagnostics(idToken: string): void {
    if (!idToken) {
      console.warn('[BasePathInterceptor] ID token missing before request');
      return;
    }

    const payload = decodeJwtPayload(idToken);
    const exp = payload?.['exp'];
    if (typeof exp !== 'number') {
      console.warn('[BasePathInterceptor] ID token present but exp claim missing/invalid');
      return;
    }

    const nowSec = Math.floor(Date.now() / 1000);
    const remainingSec = exp - nowSec;
    console.log('[BasePathInterceptor] ID token diagnostics:', {
      exp,
      expiresAtIso: new Date(exp * 1000).toISOString(),
      remainingSec,
      expired: remainingSec <= 0,
    });
  }

  /** Uses cached id token or reads from Auth0 claims when not yet in localStorage. */
  private resolveIdToken(): Observable<string> {

  return this.auth.idTokenClaims$.pipe(
    take(1),
    map((claims: any) => claims?.__raw || ''),
    catchError(() => of(''))
  );
}

  private getSilentTokenOptions(): { authorizationParams: { audience: string; scope: string } } {
    return {
      authorizationParams: {
        audience: environment.auth.authorizationParams?.audience ?? '',
        scope: 'openid profile email',
      },
    };
  }

   /**
   * Fallback for environments where idTokenClaims$ may be empty but Auth0 cache still contains id_token.
   */
  private readAuth0CachedIdToken(): string {
    try {
      let auth0CacheEntries = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.includes('@@auth0spajs@@')) {
          continue;
        }
        auth0CacheEntries++;

        const raw = localStorage.getItem(key);
        if (!raw) {
          continue;
        }

        const parsed = JSON.parse(raw);
        const candidate =
          parsed?.body?.id_token ??
          parsed?.id_token ??
          '';

        if (candidate && !isJwtExpired(candidate)) {
          console.log(
            '[BasePathInterceptor][readAuth0CachedIdToken] Found valid token in cache entry'
          );
          return candidate;
        }
        if (candidate) {
          console.warn(
            '[BasePathInterceptor][readAuth0CachedIdToken] Ignored expired token in cache entry'
          );
        }
      }
      console.log(
        '[BasePathInterceptor][readAuth0CachedIdToken] Auth0 cache entries scanned:',
        auth0CacheEntries
      );
    } catch {
      // ignore parsing issues and fallback to empty token
    }

    return '';
  }
}
